/**
 * page.ts — Blocos de domínio "Página pública" (landing page) do Studio
 *
 * Registra APENAS os blocos que são realmente de página. Texto, imagem, botão,
 * seção, colunas, divisor e espaçador continuam vindo do catálogo comum
 * (`common.*` / `layout.*`) — duplicar esses blocos por módulo é justamente o
 * que o Studio existe para evitar.
 *
 * Formulário: o bloco guarda apenas o identificador do formulário e o que fazer
 * depois do envio. A LISTA DE CAMPOS mora em
 * `document.settings.forms[formId].fields`, o que permite o mesmo formulário
 * aparecer em mais de um ponto da página e, no futuro, ser reaproveitado.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (Studio, Landing Pages, Fase 1)
 */

import { registerBlocks } from '../blockRegistry';
import type { StudioOutput } from '../types';

/** Blocos de página existem somente na saída web. */
const SOMENTE_WEB: StudioOutput[] = ['web'];

// =============================================================================
// Contratos de formulário e de configurações de página
// =============================================================================

/**
 * Tipos de campo aceitos no formulário de captura.
 * Esta lista é a FONTE ÚNICA: o tipo abaixo sai dela e o catálogo lido pelas
 * IAs também, então documentação e sistema não se separam.
 */
export const STUDIO_FORM_FIELD_TYPES = [
  { value: 'text', label: 'Texto curto' },
  { value: 'email', label: 'E-mail (validado)' },
  { value: 'phone', label: 'Telefone/WhatsApp (com máscara)' },
  { value: 'document', label: 'CPF ou CNPJ (com máscara)' },
  { value: 'number', label: 'Número' },
  { value: 'textarea', label: 'Texto longo' },
  { value: 'select', label: 'Escolha em lista (exige `options`)' },
  { value: 'checkbox', label: 'Caixa de marcação' },
  { value: 'consent', label: 'Aceite obrigatório (exige `consentText`)' },
  { value: 'hidden', label: 'Campo oculto (use `defaultValue`)' },
] as const;

/** Tipos de campo aceitos no formulário de captura. */
export type StudioFormFieldType = (typeof STUDIO_FORM_FIELD_TYPES)[number]['value'];

/** Definição de um campo do formulário. */
export interface StudioFormField {
  /** Chave gravada no envio (ex.: `nome`, `email`). */
  key: string;
  label: string;
  type: StudioFormFieldType;
  required?: boolean;
  placeholder?: string;
  /** Máscara aplicada no navegador (ex.: telefone, CPF/CNPJ). */
  mask?: string;
  /** Opções do tipo `select`. */
  options?: Array<{ value: string; label: string }>;
  /** Valor fixo para campos ocultos (ex.: origem da campanha). */
  defaultValue?: string;
  /** Texto exibido ao lado do aceite, no tipo `consent`. */
  consentText?: string;
  /** Limite de caracteres — também aplicado na validação do servidor. */
  maxLength?: number;
}

/** O que acontece depois que o visitante envia o formulário. */
export type StudioFormSuccessAction =
  | { type: 'message'; title?: string; message?: string }
  | { type: 'redirect'; url: string; delaySeconds?: number };

/** Definição completa de um formulário, guardada nas configurações do documento. */
export interface StudioFormDefinition {
  id: string;
  label?: string;
  fields: StudioFormField[];
  /** Ação padrão executada no servidor após gravar o envio. */
  defaultAction?: 'contact_upsert' | 'none';
}

/** Configurações de página (separadas do tema visual). */
export interface StudioPageSettings {
  maxWidth?: string;
  backgroundColor?: string;
  faviconUrl?: string;
  language?: string;
}

/** Campos aceitos nas configurações de página (publicado no catálogo). */
export const STUDIO_PAGE_SETTINGS_FIELDS = [
  { key: 'maxWidth', label: 'Largura do miolo da página', example: '1120px' },
  { key: 'backgroundColor', label: 'Cor de fundo da página', example: '#ffffff' },
  { key: 'faviconUrl', label: 'Ícone do site', example: 'https://exemplo.com/favicon.png' },
  { key: 'language', label: 'Idioma da página', example: 'pt-BR' },
] as const;

/** Formas de resposta depois do envio do formulário (publicado no catálogo). */
export const STUDIO_FORM_SUCCESS_ACTIONS = [
  {
    type: 'message',
    label: 'Mostrar mensagem na própria página',
    fields: ['title', 'message'],
    example: { type: 'message', title: 'Recebemos seu contato', message: 'Retornamos em até 1 hora útil.' },
  },
  {
    type: 'redirect',
    label: 'Levar o visitante para outro endereço',
    fields: ['url', 'delaySeconds'],
    example: { type: 'redirect', url: 'https://exemplo.com/obrigado', delaySeconds: 2 },
  },
] as const;

/** Configurações do documento de página pública. */
export interface StudioPageDocumentSettings {
  page?: StudioPageSettings;
  forms?: Record<string, StudioFormDefinition>;
  [key: string]: unknown;
}

/** Lê, com segurança, a definição de um formulário nas configurações. */
export function getFormDefinition(
  settings: Record<string, unknown> | undefined,
  formId: string,
): StudioFormDefinition | undefined {
  const forms = (settings as StudioPageDocumentSettings | undefined)?.forms;
  return forms?.[formId];
}

/** Lê, com segurança, as configurações de página. */
export function getPageSettings(
  settings: Record<string, unknown> | undefined,
): StudioPageSettings {
  return (settings as StudioPageDocumentSettings | undefined)?.page ?? {};
}

/** Campo oculto de armadilha (honeypot) — preenchido só por robôs. */
export const STUDIO_HONEYPOT_FIELD = 'website_company_url';

/** Formulário padrão criado junto com uma página nova. */
export const DEFAULT_LEAD_FORM: StudioFormDefinition = {
  id: 'lead_capture',
  label: 'Captura de contato',
  defaultAction: 'contact_upsert',
  fields: [
    { key: 'nome', label: 'Nome', type: 'text', required: true, maxLength: 120 },
    { key: 'email', label: 'E-mail', type: 'email', required: true, maxLength: 160 },
    { key: 'telefone', label: 'WhatsApp', type: 'phone', required: true, maxLength: 20 },
    {
      key: 'consentimento',
      label: 'Consentimento',
      type: 'consent',
      required: true,
      consentText: 'Autorizo o contato sobre esta oferta.',
    },
  ],
};

// =============================================================================
// Catálogo de blocos de página
// =============================================================================

registerBlocks([
  {
    key: 'page.hero',
    label: 'Capa da página',
    family: 'content',
    icon: 'layout-panel-top',
    outputs: SOMENTE_WEB,
    provides: ['page.hero'],
    props: [
      { key: 'title', label: 'Título', type: 'text', defaultValue: 'Título da sua oferta' },
      { key: 'subtitle', label: 'Subtítulo', type: 'text' },
      { key: 'imageUrl', label: 'Imagem de destaque', type: 'image' },
      { key: 'ctaLabel', label: 'Texto do botão', type: 'text', defaultValue: 'Quero saber mais' },
      { key: 'ctaHref', label: 'Destino do botão', type: 'text', defaultValue: '#formulario' },
      { key: 'backgroundColor', label: 'Cor de fundo', type: 'color' },
      { key: 'textColor', label: 'Cor do texto', type: 'color' },
      {
        key: 'align',
        label: 'Alinhamento',
        type: 'select',
        defaultValue: 'center',
        options: [
          { value: 'left', label: 'Esquerda' },
          { value: 'center', label: 'Centro' },
        ],
      },
    ],
  },
  {
    key: 'page.features',
    label: 'Lista de destaques',
    family: 'content',
    icon: 'list-checks',
    outputs: SOMENTE_WEB,
    provides: ['page.features'],
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text', defaultValue: 'Por que escolher' },
      {
        key: 'items',
        label: 'Itens (um por linha, use “|” para separar título e descrição)',
        type: 'richtext',
        defaultValue: 'Atendimento rápido|Resposta em até 1 hora útil',
        help: 'Exemplo: Suporte 24h|Equipe disponível todos os dias',
        syntax: 'Um destaque por linha: titulo|descricao. Sem o | a linha vira só título.',
        example: 'Atendimento rápido|Resposta em até 1 hora útil\nCobertura nacional|Mais de 3.000 cidades',
      },
      {
        key: 'columns',
        label: 'Colunas',
        type: 'select',
        defaultValue: '3',
        options: [
          { value: '1', label: '1 coluna' },
          { value: '2', label: '2 colunas' },
          { value: '3', label: '3 colunas' },
        ],
      },
    ],
  },
  {
    key: 'page.testimonials',
    label: 'Depoimentos',
    family: 'content',
    icon: 'quote',
    outputs: SOMENTE_WEB,
    provides: ['page.testimonials'],
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text', defaultValue: 'O que dizem sobre nós' },
      {
        key: 'items',
        label: 'Depoimentos (um por linha: texto|nome|empresa)',
        type: 'richtext',
        syntax: 'Um depoimento por linha: texto|nome|empresa.',
        example: 'Atendimento impecável do início ao fim.|Maria Souza|Transportes Souza',
        defaultValue: 'Atendimento impecável.|Maria Souza|Transportes Souza',
      },
    ],
  },
  {
    key: 'page.form',
    label: 'Formulário',
    family: 'action',
    icon: 'clipboard-list',
    outputs: SOMENTE_WEB,
    provides: ['page.form'],
    props: [
      { key: 'formId', label: 'Identificador do formulário', type: 'text', defaultValue: 'lead_capture' },
      { key: 'title', label: 'Título do bloco', type: 'text', defaultValue: 'Fale com a gente' },
      { key: 'description', label: 'Texto de apoio', type: 'text' },
      { key: 'submitLabel', label: 'Texto do botão', type: 'text', defaultValue: 'Enviar' },
      {
        key: 'successType',
        label: 'Depois do envio',
        type: 'select',
        defaultValue: 'message',
        options: [
          { value: 'message', label: 'Mostrar mensagem' },
          { value: 'redirect', label: 'Redirecionar' },
        ],
      },
      { key: 'successTitle', label: 'Título da mensagem', type: 'text', defaultValue: 'Obrigado!' },
      {
        key: 'successMessage',
        label: 'Mensagem de sucesso',
        type: 'text',
        defaultValue: 'Recebemos seus dados. Em breve entraremos em contato.',
      },
      { key: 'successUrl', label: 'Endereço de redirecionamento', type: 'text' },
    ],
  },
]);
